import { useRef } from 'react';

import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
/**
 * EditorialRail
 *
 * Horizontal-scroll rail of curated Featured Sections shown at the top of
 * the Browse content area. Always visible, regardless of filters, acts
 * as the editorial counterpart to the unified product grid.
 *
 * Decks are intentionally NOT shown here, they appear interspersed in
 * the product grid below, plus they get their own visual treatment via
 * fan-out hover, which doesn't translate to a compact rail tile.
 */
export default function EditorialRail({
  featuredSections,
  darkMode,
  onFeaturedClick,
  activeFeaturedId
}) {
  const scrollRef = useRef(null);

  const activeFeatured = featuredSections.filter((f) => f.is_active);

  if (activeFeatured.length === 0) return null;

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({ left: dir === 'next' ? w * 0.8 : -w * 0.8, behavior: 'smooth' });
  };

  return (
    <div className="mb-8" data-testid="editorial-rail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2
            className="text-2xl sm:text-3xl transition-colors"
            style={{
              fontFamily: 'Playfair Display, serif',
              color: darkMode.textPrimary,
              fontWeight: '600'
            }}
          >
            Curated for you
          </h2>
          <p className="text-sm mt-1 transition-colors" style={{ color: darkMode.textSecondary }}>
            Themed collections, hand-picked for the moment
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => scroll('prev')}
            className="p-2 rounded-full transition-all hover:scale-105 hover:transform-none"
            style={{ background: darkMode.bgSecondary, border: `1px solid ${darkMode.borderPrimary}` }}
            aria-label="Scroll left"
            data-testid="editorial-rail-prev"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: darkMode.textPrimary }} />
          </button>
          <button
            onClick={() => scroll('next')}
            className="p-2 rounded-full transition-all hover:scale-105 hover:transform-none"
            style={{ background: darkMode.bgSecondary, border: `1px solid ${darkMode.borderPrimary}` }}
            aria-label="Scroll right"
            data-testid="editorial-rail-next"
          >
            <ChevronRight className="w-5 h-5" style={{ color: darkMode.textPrimary }} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {activeFeatured.map((featured) => {
          const itemCount = featured.item_ids?.length || 0;
          const isActive = activeFeaturedId === featured.id;
          const isDark = darkMode.isDark;
          return (
            <button
              key={`rail-feat-${featured.id}`}
              onClick={() => onFeaturedClick && onFeaturedClick(featured)}
              className="snap-start flex-shrink-0 w-[280px] sm:w-[320px] text-left rounded-2xl p-5 transition-shadow duration-200 hover:transform-none"
              style={{
                // Light mode keeps the rail airy, white tiles with a warm
                // peach frame. Dark mode retains the original deep gradient.
                background: isDark
                  ? 'linear-gradient(135deg, #2D4354 0%, #534145 100%)'
                  : '#FFFFFF',
                // Border stays identical between states to avoid layout
                // shift / color-flash mid-transition. The active state is
                // communicated via an inset cream glow that brightens the
                // tile's interior edges, no outer halo, no geometric
                // seams.
                border: '2px solid #FED7A5',
                boxShadow: isActive
                  ? (isDark
                      ? 'inset 0 0 24px 2px rgba(254, 215, 165, 0.45), 0 8px 24px rgba(45, 67, 84, 0.3)'
                      : 'inset 0 0 24px 2px rgba(254, 215, 165, 0.5), 0 8px 24px rgba(158, 103, 82, 0.18)')
                  : (isDark
                      ? '0 8px 24px rgba(45, 67, 84, 0.3)'
                      : '0 6px 18px rgba(158, 103, 82, 0.12)'),
                minHeight: '140px'
              }}
              data-testid={`rail-featured-${featured.id}`}
              aria-pressed={isActive}
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4" style={{ color: isDark ? '#FED7A5' : '#9E6752' }} />
                <span
                  className="text-xs tracking-widest uppercase"
                  style={{ color: isDark ? '#FED7A5' : '#9E6752', fontWeight: '600' }}
                >
                  Featured
                </span>
              </div>
              <h3
                className="text-lg sm:text-xl mb-2 leading-tight"
                style={{ fontFamily: 'Playfair Display, serif', color: isDark ? '#FED7A5' : '#20212B', fontWeight: '700' }}
              >
                {featured.name}
              </h3>
              {featured.description && (
                <p
                  className="text-sm line-clamp-2"
                  style={{ color: isDark ? '#E5E5E5' : '#534145', fontWeight: '500' }}
                >
                  {featured.description}
                </p>
              )}
              <p className="text-xs mt-2" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#73766A' }}>
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
